import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTrip } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft } from '@phosphor-icons/react';
import { computeIndividualLeaderboard, computeStablefordLeague, computeNSPLeague, teamRoundPoints, teamRoundPointsThrough, effectiveHolePars, isRoundFrozen, revealHoleForRound, computeTripTeamStandings, formatHandicapIndex } from '../golf';
import { playerCountingContribution } from '../lib/teamContribution';
import { roundFormat, effectiveTeamScoring } from '../lib/formatHelpers';
import { groupDisplayName } from '../lib/groupOrder';
import { effectiveCourseForPlayer, effectiveStrokeIndexesForPlayer } from '../lib/effectiveCourse';

/* ── Shared position cell ── */
function PosCell({ rank, hasScores, isTied }) {
  if (!hasScores) return <span className="text-xs font-bold text-[#B8C7C0] tabular-nums">{'\u2013'}</span>;
  const label = isTied ? `T${rank}` : String(rank);
  return (
    <div className="flex items-center justify-center gap-0.5">
      {rank === 1 && <span className="w-2 h-2 rounded-full bg-[#D4AF37] inline-block flex-shrink-0" />}
      {rank === 2 && <span className="w-2 h-2 rounded-full bg-[#C0C0C0] inline-block flex-shrink-0" />}
      {rank === 3 && <span className="w-2 h-2 rounded-full bg-[#B08D57] inline-block flex-shrink-0" />}
      <span className="text-xs font-bold text-[#1D242B] tabular-nums">{label}</span>
    </div>
  );
}

export default function LeagueTable() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('stableford');

  const load = useCallback(async () => {
    try { const res = await getTrip(code); setTrip(res.data); }
    catch {} finally { setLoading(false); }
  }, [code]);
  useEffect(() => { load(); }, [load]);

  const players = trip?.players || [];
  const rounds = trip?.rounds || [];

  // ── Data computation (unchanged) ──
  const stablefordRounds = rounds.filter(r => roundFormat(r) === 'stableford' && r.scoring_mode !== 'texas_scramble');
  const nspRounds = rounds.filter(r => roundFormat(r) === 'nsp' && r.scoring_mode !== 'texas_scramble');

  const stablefordTable = (() => {
    if (!trip || stablefordRounds.length === 0) return [];
    const entries = computeStablefordLeague(trip);
    return entries.map(e => {
      const player = players.find(p => p.id === e.player_id);
      const roundTotals = stablefordRounds.map(r => {
        if ((r.nr_players || []).includes(e.player_id)) return 'NR';
        return e.per_round[r.id] || 0;
      });
      const total = roundTotals.reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0);
      return {
        playerId: e.player_id, playerName: e.name,
        whsIndex: player?.whs_index ?? 0, handicapMode: player?.handicap_mode || 'whs', position: e.rank ?? 0,
        roundTotals, grandTotal: total, cb: e.cb,
      };
    });
  })();

  const nspTable = (() => {
    if (!trip || nspRounds.length === 0) return [];
    const entries = computeNSPLeague(trip, effectiveCourseForPlayer, effectiveStrokeIndexesForPlayer);
    return entries.map(e => {
      const player = players.find(p => p.id === e.player_id);
      const roundTotals = nspRounds.map(r => {
        if ((r.nr_players || []).includes(e.player_id)) return 'NR';
        return e.per_round[r.id] || 0;
      });
      const countedRounds = roundTotals.filter(v => typeof v === 'number' && v !== 0);
      const total = countedRounds.reduce((s, v) => s + v, 0);
      return {
        playerId: e.player_id, playerName: e.name,
        whsIndex: player?.whs_index ?? 0, handicapMode: player?.handicap_mode || 'whs', position: e.rank ?? 0,
        roundTotals, grandTotal: total,
      };
    });
  })();

  // Per-round team standings (groups tab)
  const teamRounds = rounds.filter(r => effectiveTeamScoring(trip, r) && roundFormat(r) !== 'nsp');
  const teamSections = teamRounds.map((round) => {
    if (!round.teams || round.teams.length === 0) {
      return { round, allTeams: [], hasTeams: false, frozen: false };
    }
    const frozen = isRoundFrozen(round);
    const hp = effectiveHolePars(round);
    const ranked = frozen && !round.revealed
      ? teamRoundPointsThrough(round.teams, round.hole_scores, hp, revealHoleForRound(round) - 1)
      : teamRoundPoints(round.teams, round.scores || {}, round.hole_scores, hp, round.hole_nr, round);

    const rankedByIdx = new Map(ranked.map(t => [t.team_index, t]));
    const allTeams = round.teams.map((teamPlayers, idx) => {
      const r = rankedByIdx.get(idx);
      if (r) return { ...r, hasScored: true };
      return { team_index: idx, player_ids: teamPlayers, stableford_total: 0, rank: null, hasScored: false };
    });
    allTeams.sort((a, b) => {
      if (a.hasScored !== b.hasScored) return a.hasScored ? -1 : 1;
      if (a.hasScored && b.hasScored) return (a.rank || 0) - (b.rank || 0);
      return a.team_index - b.team_index;
    });

    return { round, allTeams, hasTeams: true, frozen: frozen && !round.revealed };
  });

  // Trip Teams standings (persistent cross-round)
  const hasTripTeams = (trip?.trip_teams?.length || 0) > 0;
  const tripTeamStandings = hasTripTeams
    ? computeTripTeamStandings(trip, effectiveCourseForPlayer, effectiveStrokeIndexesForPlayer)
    : [];
  const sortedTripTeams = [...tripTeamStandings].sort((a, b) => {
    if (b.stableford_total !== a.stableford_total) return b.stableford_total - a.stableford_total;
    return a.nsp_total - b.nsp_total;
  });

  // ── Division filtering for league ──
  const divisions = trip?.divisions || [];
  const divEnabled = trip?.divisions_enabled && divisions.length > 0;
  const [divisionTab, setDivisionTab] = useState('overall');
  const playerDivMap = {};
  if (divEnabled) {
    for (const p of players) playerDivMap[p.id] = p.division_id || null;
  }

  const filterLeagueByDiv = (table, divId) => {
    if (!divId || divId === 'overall') return table;
    const filtered = table.filter(e => playerDivMap[e.playerId] === divId);
    // Re-rank within division
    return filtered.map((e, i, arr) => {
      if (i === 0) return { ...e, position: 1 };
      const prev = arr[i-1];
      return { ...e, position: e.grandTotal === prev.grandTotal ? prev.position : i + 1 };
    });
  };

  const displayStableford = divEnabled ? filterLeagueByDiv(stablefordTable, divisionTab) : stablefordTable;
  const displayNsp = divEnabled ? filterLeagueByDiv(nspTable, divisionTab) : nspTable;

  // ── Tie helpers ──
  const buildTieMap = (entries, posKey = 'position') => {
    const counts = {};
    entries.forEach(e => { const p = e[posKey]; counts[p] = (counts[p] || 0) + 1; });
    return (pos) => (counts[pos] || 0) > 1;
  };

  if (loading) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#F4F6F5] pb-safe" data-testid="league-table-screen">
      <div className="bg-[#1D242B] px-4 py-4 flex items-center gap-3">
        <button onClick={() => navigate(`/trip/${code}`)} className="text-white p-1" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" weight="bold" />
        </button>
        <div className="flex-1"><h1 className="text-xl text-white font-bold">League Table</h1><p className="text-xs text-[#B8C7C0]">{trip?.name}</p></div>
      </div>

      <div className="px-4 pt-4 max-w-[900px] mx-auto">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full bg-[#F4F6F5] rounded-xl h-11 mb-4">
            <TabsTrigger value="stableford" className="flex-1 rounded-lg text-sm font-semibold data-[state=active]:bg-white" data-testid="tab-stableford">Stableford</TabsTrigger>
            <TabsTrigger value="nsp" className="flex-1 rounded-lg text-sm font-semibold data-[state=active]:bg-white" data-testid="tab-nsp">Net Score</TabsTrigger>
            {hasTripTeams && <TabsTrigger value="trip_teams" className="flex-1 rounded-lg text-sm font-semibold data-[state=active]:bg-white" data-testid="tab-trip-teams">Trip Teams</TabsTrigger>}
            <TabsTrigger value="teams" className="flex-1 rounded-lg text-sm font-semibold data-[state=active]:bg-white" data-testid="tab-teams">Groups</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Division tabs (league-wide) */}
        {divEnabled && (tab === 'stableford' || tab === 'nsp') && (
          <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1" data-testid="league-division-tabs">
            <button onClick={() => setDivisionTab('overall')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${divisionTab === 'overall' ? 'bg-[#1B4332] text-white' : 'bg-[#F4F6F5] text-[#455561]'}`}
              data-testid="league-div-tab-overall">Overall</button>
            {divisions.map(d => (
              <button key={d.id} onClick={() => setDivisionTab(d.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${divisionTab === d.id ? 'bg-[#1B4332] text-white' : 'bg-[#F4F6F5] text-[#455561]'}`}
                data-testid={`league-div-tab-${d.id}`}>{d.name}</button>
            ))}
          </div>
        )}

        {/* ══════════ Stableford Tab ══════════ */}
        {tab === 'stableford' && (
          <>
            {stablefordRounds.length === 0 ? (
              <div className="rounded-xl border border-[#E1E6E4] bg-white p-8 text-center">
                <p className="text-[#455561] text-sm">No rounds using Stableford format yet.</p>
              </div>
            ) : (() => {
              const isTied = buildTieMap(displayStableford.filter(e => e.grandTotal > 0));
              return (
                <div className="rounded-xl overflow-hidden border border-[#E1E6E4]" data-testid="stableford-league-table">
                  {/* Header */}
                  <div className="flex items-center bg-[#1B4332] text-white text-[10px] font-semibold tracking-wide px-3 py-2">
                    <div className="w-9 flex-shrink-0 text-center">POS</div>
                    <div className="flex-1 min-w-0">PLAYER</div>
                    {stablefordRounds.map((r, i) => (
                      <div key={i} className="w-10 text-center flex-shrink-0">R{rounds.indexOf(r) + 1}</div>
                    ))}
                    <div className="w-12 text-center flex-shrink-0">TOTAL</div>
                  </div>
                  {displayStableford.length === 0 ? (
                    <div className="p-8 text-center bg-white"><p className="text-[#455561] text-sm">No scores recorded yet</p></div>
                  ) : (
                    displayStableford.map((entry, idx) => {
                      const hasScores = entry.grandTotal > 0;
                      const evenRow = idx % 2 === 0;
                      return (
                        <div key={entry.playerId}
                          className={`flex items-start px-3 py-2 border-t border-[#E1E6E4] ${evenRow ? 'bg-white' : 'bg-[#F7F9F8]'}`}
                          data-testid={`league-row-${entry.playerId}`}>
                          <div className="w-9 flex-shrink-0 text-center pt-0.5">
                            <PosCell rank={entry.position} hasScores={hasScores} isTied={isTied(entry.position)} />
                          </div>
                          <div className="flex-1 min-w-0 pr-1">
                            <div className="flex items-center gap-1">
                              <span className="text-sm font-bold text-[#1D242B] overflow-hidden text-ellipsis">{entry.playerName}</span>
                              {entry.cb && <Badge className="bg-[#007AFF] text-white text-[7px] px-1 py-0 font-bold flex-shrink-0">CB</Badge>}
                            </div>
                            <p className="text-[10px] text-[#8A9BA3]">WHS {entry.handicapMode === 'ph' ? '\u2013' : formatHandicapIndex(entry.whsIndex)}</p>
                          </div>
                          {entry.roundTotals.map((rt, i) => (
                            <div key={i} className="w-10 text-center flex-shrink-0 pt-0.5">
                              <span className={`text-xs tabular-nums font-medium ${rt === 'NR' ? 'text-[#B8C7C0]' : rt === 0 ? 'text-[#B8C7C0]' : 'text-[#455561]'}`}>
                                {rt === 'NR' ? 'NR' : rt === 0 ? '\u2013' : rt}
                              </span>
                            </div>
                          ))}
                          <div className="w-12 text-center flex-shrink-0 pt-0.5">
                            <span className={`text-base font-black tabular-nums ${hasScores ? 'text-[#1D242B]' : 'text-[#B8C7C0]'}`}>
                              {hasScores ? entry.grandTotal : '\u2013'}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              );
            })()}
          </>
        )}

        {/* ══════════ NSP Tab ══════════ */}
        {tab === 'nsp' && (
          <>
            {nspRounds.length === 0 ? (
              <div className="rounded-xl border border-[#E1E6E4] bg-white p-8 text-center">
                <p className="text-[#455561] text-sm">No rounds using Net Stroke Play format yet.</p>
              </div>
            ) : (() => {
              const isTied = buildTieMap(nspTable);
              // NSP: all entries have scores (filtered by compute)
              return (
                <div className="rounded-xl overflow-hidden border border-[#E1E6E4]" data-testid="nsp-league-table">
                  <div className="flex items-center bg-[#1B4332] text-white text-[10px] font-semibold tracking-wide px-3 py-2">
                    <div className="w-9 flex-shrink-0 text-center">POS</div>
                    <div className="flex-1 min-w-0">PLAYER</div>
                    {nspRounds.map((r, i) => (
                      <div key={i} className="w-10 text-center flex-shrink-0">R{rounds.indexOf(r) + 1}</div>
                    ))}
                    <div className="w-12 text-center flex-shrink-0">NET</div>
                  </div>
                  {displayNsp.length === 0 ? (
                    <div className="p-8 text-center bg-white"><p className="text-[#455561] text-sm">No scores recorded yet</p></div>
                  ) : (
                    displayNsp.map((entry, idx) => {
                      const hasScores = entry.roundTotals.some(rt => typeof rt === 'number' && rt !== 0);
                      const evenRow = idx % 2 === 0;
                      return (
                        <div key={entry.playerId}
                          className={`flex items-start px-3 py-2 border-t border-[#E1E6E4] ${evenRow ? 'bg-white' : 'bg-[#F7F9F8]'}`}
                          data-testid={`nsp-league-row-${entry.playerId}`}>
                          <div className="w-9 flex-shrink-0 text-center pt-0.5">
                            <PosCell rank={entry.position} hasScores={hasScores} isTied={isTied(entry.position)} />
                          </div>
                          <div className="flex-1 min-w-0 pr-1">
                            <span className="text-sm font-bold text-[#1D242B] overflow-hidden text-ellipsis block">{entry.playerName}</span>
                            <p className="text-[10px] text-[#8A9BA3]">WHS {entry.handicapMode === 'ph' ? '\u2013' : formatHandicapIndex(entry.whsIndex)}</p>
                          </div>
                          {entry.roundTotals.map((rt, i) => (
                            <div key={i} className="w-10 text-center flex-shrink-0 pt-0.5">
                              <span className={`text-xs tabular-nums font-medium ${rt === 'NR' ? 'text-[#B8C7C0]' : rt === 0 ? 'text-[#B8C7C0]' : 'text-[#455561]'}`}>
                                {rt === 'NR' ? 'NR' : rt === 0 ? '\u2013' : rt}
                              </span>
                            </div>
                          ))}
                          <div className="w-12 text-center flex-shrink-0 pt-0.5">
                            <span className={`text-base font-black tabular-nums ${hasScores ? 'text-[#1D242B]' : 'text-[#B8C7C0]'}`}>
                              {hasScores ? entry.grandTotal : '\u2013'}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              );
            })()}
          </>
        )}

        {/* ══════════ Groups Tab ══════════ */}
        {tab === 'teams' && (
          <div className="space-y-6">
            {teamSections.length === 0 && (
              <div className="rounded-xl border border-[#E1E6E4] bg-white p-8 text-center">
                <p className="text-[#455561] text-sm">No team-scored rounds yet.</p>
              </div>
            )}
            {teamSections.map(({ round: r, allTeams, hasTeams, frozen }, ri) => {
              const anyScored = allTeams.some(t => t.hasScored);
              // Build tie map for team ranks
              const teamTieMap = (() => {
                const counts = {};
                allTeams.filter(t => t.hasScored).forEach(t => { counts[t.rank] = (counts[t.rank] || 0) + 1; });
                return (rank) => (counts[rank] || 0) > 1;
              })();

              return (
                <div key={r.id} data-testid={`team-section-${ri}`}>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h3 className="font-bold text-[#1D242B] text-sm">{r.label}</h3>
                    <span className="text-xs text-[#455561]">{r.course?.name}</span>
                    {r.revealed ? (
                      <Badge className="bg-[#1B4332] text-white text-[10px]">Final</Badge>
                    ) : frozen ? (
                      <Badge className="bg-[#E59000] text-white text-[10px]">Frozen</Badge>
                    ) : anyScored ? (
                      <Badge className="bg-[#007AFF] text-white text-[10px]">Live</Badge>
                    ) : hasTeams ? (
                      <Badge className="bg-[#B8C7C0] text-white text-[10px]">Not started</Badge>
                    ) : null}
                  </div>

                  {!hasTeams ? (
                    <div className="rounded-xl border border-[#E1E6E4] bg-white p-6 text-center">
                      <p className="text-sm text-[#455561]">Teams not drawn yet.</p>
                    </div>
                  ) : (
                    <div className="rounded-xl overflow-hidden border border-[#E1E6E4]" data-testid={`team-league-table-${ri}`}>
                      <div className="flex items-center bg-[#1B4332] text-white text-[10px] font-semibold tracking-wide px-3 py-2">
                        <div className="w-9 flex-shrink-0 text-center">POS</div>
                        <div className="flex-1 min-w-0">TEAM</div>
                        <div className="w-14 text-center flex-shrink-0">PTS</div>
                      </div>
                      {allTeams.map((te, idx) => {
                        const evenRow = idx % 2 === 0;
                        const teamPlayerNames = (te.player_ids || []).map(pid => {
                          const p = players.find(pl => pl.id === pid);
                          return p?.name || '?';
                        });
                        return (
                          <div key={te.team_index}
                            className={`flex items-start px-3 py-2 border-t border-[#E1E6E4] ${evenRow ? 'bg-white' : 'bg-[#F7F9F8]'}`}
                            data-testid={`team-league-${ri}-${te.team_index}`}>
                            <div className="w-9 flex-shrink-0 text-center pt-0.5">
                              <PosCell rank={te.rank} hasScores={te.hasScored} isTied={te.hasScored && teamTieMap(te.rank)} />
                            </div>
                            <div className="flex-1 min-w-0 pr-1">
                              <div className="flex items-center gap-1">
                                <span className="text-sm font-bold text-[#1D242B] overflow-hidden text-ellipsis">{groupDisplayName(r, te.team_index)}</span>
                                {te.cb && <Badge className="bg-[#007AFF] text-white text-[7px] px-1 py-0 font-bold flex-shrink-0">CB</Badge>}
                                {!te.hasScored && <Badge className="bg-[#F4F6F5] text-[#B8C7C0] text-[9px] flex-shrink-0">Not started</Badge>}
                              </div>
                              <p className="text-[10px] text-[#8A9BA3] flex flex-wrap gap-x-1 leading-snug">{teamPlayerNames.map((n, i) => <span key={i} className="whitespace-nowrap">{i > 0 ? '· ' : ''}{n}</span>)}</p>
                            </div>
                            <div className="w-14 text-center flex-shrink-0 pt-0.5">
                              <span className={`text-base font-black tabular-nums ${te.hasScored ? 'text-[#1D242B]' : 'text-[#B8C7C0]'}`}>
                                {te.hasScored ? te.stableford_total : '\u2013'}
                              </span>
                              {te.hasScored && <span className="text-[9px] text-[#455561] block">pts</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ══════════ Trip Teams Tab (unchanged) ══════════ */}
        {tab === 'trip_teams' && hasTripTeams && (
          <div className="space-y-3" data-testid="trip-teams-standings">
            {sortedTripTeams.every(t => t.stableford_total === 0 && t.nsp_total === 0) ? (
              <div className="rounded-xl border border-[#E1E6E4] bg-white p-8 text-center">
                <p className="text-[#455561] text-sm">No scores recorded yet for trip teams.</p>
              </div>
            ) : (
              sortedTripTeams.map((team, ri) => {
                const isTied = sortedTripTeams.filter(t =>
                  t.stableford_total === team.stableford_total && t.nsp_total === team.nsp_total
                ).length > 1;
                const pos = ri + 1;
                return (
                  <div key={team.team_id}
                    className="rounded-xl overflow-hidden border border-[#E1E6E4]"
                    style={{ borderLeftColor: team.color, borderLeftWidth: 4 }}
                    data-testid={`trip-team-standing-${ri}`}>
                    <div className="p-4 bg-white">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <PosCell rank={pos} hasScores={team.stableford_total > 0 || team.nsp_total !== 0} isTied={isTied} />
                          <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: team.color }} />
                          <span className="font-semibold text-[#1D242B]">{team.team_name}</span>
                          <Badge className="text-[10px] bg-[#F4F6F5] text-[#455561]">{team.member_count}</Badge>
                        </div>
                      </div>
                      <div className="flex gap-6">
                        {team.stableford_rounds > 0 && (
                          <div>
                            <span className="text-xl font-bold text-[#1B4332] tabular-nums">{team.stableford_total}</span>
                            <span className="text-xs text-[#455561] ml-1">SF pts</span>
                            <span className="text-[10px] text-[#B8C7C0] ml-1">({team.stableford_rounds}R)</span>
                          </div>
                        )}
                        {team.nsp_rounds > 0 && (
                          <div>
                            <span className="text-xl font-bold text-[#1B4332] tabular-nums">{team.nsp_total}</span>
                            <span className="text-xs text-[#455561] ml-1">NSP net</span>
                            <span className="text-[10px] text-[#B8C7C0] ml-1">({team.nsp_rounds}R)</span>
                          </div>
                        )}
                        {team.stableford_rounds === 0 && team.nsp_rounds === 0 && (
                          <span className="text-sm text-[#B8C7C0]">No scores yet</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(team.members || []).map(m => {
                          const hasSf = team.stableford_rounds > 0 && m.stableford > 0;
                          const hasNsp = team.nsp_rounds > 0;
                          const hasAny = hasSf || (hasNsp && m.nsp !== 0);
                          return (
                            <span key={m.player_id} className="text-[10px] bg-[#F4F6F5] px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                              <span className="text-[#455561]">{m.name}</span>
                              {hasAny ? (
                                <>
                                  {hasSf && <span className="text-[#1B4332] font-bold">{m.stableford}pts</span>}
                                  {hasNsp && m.nsp !== 0 && <span className="text-[#1B4332] font-bold">{m.nsp}net</span>}
                                </>
                              ) : (
                                <span className="text-[#B8C7C0] font-bold">{'\u2014'}</span>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
